import React, { Suspense, useState } from "react";
import { PageLoader } from "../Components";
import { useDispatch, useSelector } from "react-redux";
import useRestrictionsConfig from "@/Hooks/useRestrictionsConfig";
import LocalStorageHelper from "@/Utils/LocalStorageHelper";
import { ORDER_DATE_RANGE, SETTLEMENT_DATE_RANGE } from "@/Constants/localStorageKeys";
import { RootState } from "@/Redux/Reducers";
import history from "../Utils/history";
import { getLoginUrl } from "@/Utils/common";
import { authenticateUser, saveStoreInfo, updateUserInvitation } from "@/Redux/Actions";
import { getAuthToken } from "@/Utils/authToken";
import { BUY_SUBSCRIPTION } from "@/Constants/routesConstant";

const PremiumSearchModal = React.lazy(() => import("@/Components/PremiumSearchModal"));
const VariantSelectedListModal = React.lazy(() => import("@/Components/VariantSelectedListModal"));
const AddVariantListModal = React.lazy(() => import("@/Components/AddVariantListModal"));
const BillContactModal = React.lazy(() => import("@/Components/BillContactModal"));
const CreateBillSuccessModal = React.lazy(() => import("@/Components/CreateBillSuccessModal"));
const RequestCallbackSuccessPopup = React.lazy(() => import("@/Components/RequestCallbackSuccessPopup"));
const DomainRenewalModal = React.lazy(() => import("@/Components/DomainRenewalModal"));
const WorkspaceExpiryModal = React.lazy(() => import("@/Components/WorkspaceExpiryModal"));
const SubscriptionExpiryModal = React.lazy(() => import("@/Components/SubscriptionExpiryModal"));
const StaffInvitationModal = React.lazy(() => import("../Components/StaffInvitationModal"));
const RestrictionsModal = React.lazy(() => import("@/Components/RestrictionsModal"));
const SubscriptionRequestCallbackModal = React.lazy(
  () => import("../CommonElements/Modal/SubscriptionRequestCallbackModal/SubscriptionRequestCallbackModal"),
);

import BulkUploadSuccessModal from "../Components/BulkUpload/bulkUploadSuccessModal";

export function GlobalModalsContainer() {
  const dispatch = useDispatch();

  const [showStaffInvitationModal, setShowStaffInvitationModal] = useState(false);
  const {
    staffInvitationData,
    storeData,
    showPremiumSearchModal,
    variantModalVisibility,
    isBillContactModalVisible,
    workspaceExpiryModalView,
    domainRenewalModalView,
    showRequestCallbackModal,
    ecommEliteCallbackModal,
    selectedVariantsModal,
    isCreateBillModalVisible,
    showSubscriptionExpireModal,
  } = useSelector((state: RootState) => ({
    staffInvitationData: state.staffManagementReducer.staffInvitationData,
    storeData: state.storeReducer.store,
    showPremiumSearchModal: state.commonReducer.showPremiumSearchModal,
    variantModalVisibility: state.commonReducer?.variant?.variantModal,
    isBillContactModalVisible: state.commonReducer.isBillContactModalVisible,
    workspaceExpiryModalView: state.commonReducer.workspaceExpiryModalView,
    domainRenewalModalView: state.commonReducer.domainRenewalModalView,
    showRequestCallbackModal: state.subscriptionReducer.showRequestCallbackModal,
    ecommEliteCallbackModal: state.commonReducer.ecommEliteCallbackModal,
    selectedVariantsModal: state.commonReducer.selectedVariantsModal,
    isCreateBillModalVisible: state.commonReducer.isCreateBillModalVisible,
    showSubscriptionExpireModal: state.subscriptionReducer.showSubscriptionExpireModal,
  }));

  const { homeScreenModalConfig } = useRestrictionsConfig();

  const onInvitationAction = (actionType: any) => {
    if (!actionType) {
      setTimeout(() => {
        LocalStorageHelper.delete("auth_token");
        LocalStorageHelper.delete("state");
        LocalStorageHelper.delete(SETTLEMENT_DATE_RANGE);
        LocalStorageHelper.delete(ORDER_DATE_RANGE);
        history.push(getLoginUrl());
        location.reload();
      }, 1000);
    } else {
      const dataToPost = {
        store_id: staffInvitationData?.staff_invitation?.invited_store_id,
        user_id: storeData?.user_id,
        status: actionType,
      };
      const callback = (response: any) => {
        setShowStaffInvitationModal(false);
        // if accepted
        if (actionType === 1) {
          dispatch(saveStoreInfo(response));
          if (window.location.pathname.includes("/login")) {
            history.push("/orders");
          }
          getAuthToken() && dispatch(authenticateUser(getAuthToken(), history));
        } else {
          if (getAuthToken()) {
            if (window.location.pathname.includes("/login")) {
              history.push("/orders");
            }
          } else {
            if (window.location.pathname !== BUY_SUBSCRIPTION) {
              history.push(getLoginUrl());
            }
          }
        }
      };
      dispatch(updateUserInvitation(dataToPost, callback));
    }
  };

  return (
    <>
      <Suspense fallback={<PageLoader show={true} />}>
        {showPremiumSearchModal && <PremiumSearchModal />}
        {variantModalVisibility && <AddVariantListModal />}
        {selectedVariantsModal && <VariantSelectedListModal />}
        {isBillContactModalVisible && <BillContactModal />}
        {isCreateBillModalVisible && <CreateBillSuccessModal />}
        {showStaffInvitationModal && 
          <StaffInvitationModal
            show={showStaffInvitationModal}
            onClose={onInvitationAction}
            staffData={staffInvitationData?.staff_invitation}
          />
        }
        {workspaceExpiryModalView && <WorkspaceExpiryModal />}
        {domainRenewalModalView && <DomainRenewalModal />}
        {showSubscriptionExpireModal && <SubscriptionExpiryModal />}
        {showRequestCallbackModal && <RequestCallbackSuccessPopup inRootTree={true} />}
        {homeScreenModalConfig.isVisible && <RestrictionsModal {...homeScreenModalConfig} />}
        {!!ecommEliteCallbackModal?.view && <SubscriptionRequestCallbackModal />}
      </Suspense>
      <BulkUploadSuccessModal />
    </>
  );
}
